import { isMqDesktop, isMqMobile } from 'mk/common/responsive';
import {
    getLastKnownUsernameCookie,
    getPregnancyNewsletterClosedCookie,
    getPregnancyNewsletterCookie,
} from 'mk2/apps/users/utils';
import ErrorBoundary from 'mk2/components/ErrorBoundary';
import { Route } from 'mk2/containers/Application/Application';
import { getLogger } from 'mk2/logger';
import { AppState } from 'mk2/reducers';
import { initBrowserHistory } from 'mk2/services/browserHistory';
import { initJam } from 'mk2/services/jam';
import { initStore } from 'mk2/store';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import * as Loadable from 'react-loadable';
import { Provider } from 'react-redux';
import { matchPath } from 'react-router';
import { ConnectedRouter } from 'react-router-redux';

const logger = getLogger('helpers/init');

export async function appInit(
    AppComponent: React.ComponentClass<any>,
    routes: Route[],
    rootSagas: any,
) {
    const initialState: Partial<AppState> = typeof window !== 'undefined' ? (window as any).INITIAL_STATE : {};

    const mobile = isMqMobile();
    const desktop = isMqDesktop();

    if (initialState.request && initialState.request.device
        && (mobile !== initialState.request.device.mobile || desktop !== initialState.request.device.desktop)) {
        logger.warn('Device detection from server is different from client', { server: initialState.request.device, client: { mobile, desktop } });
    }

    // Detect mobile / desktop
    initialState.request = {
        ...initialState.request,
        userAgent: navigator.userAgent,
        device: {
            ...(initialState.request && initialState.request.device),
            mobile,
            desktop,
            // Save initial screen size to state
            screen: {
                width: window.screen.availWidth,
                height: window.screen.availHeight,
            },
            // Save initial viewport size to state
            viewport: {
                width: document.documentElement.clientWidth || window.innerWidth,
                height: document.documentElement.clientHeight || window.innerHeight,
            },
            keyboard: {
                opened: false,
                height: 200,
            },
        },
        lastKnownUsername: getLastKnownUsernameCookie(),
        pregnancyNewsletterSubscribed: getPregnancyNewsletterCookie(),
        pregnancyNewsletterClosed: getPregnancyNewsletterClosedCookie(),
    };

    // Set cookie, so that we can use it during server side rendering
    document.cookie = `ssr_device=${mobile ? '1' : '0'}${desktop ? '1' : '0'}; path=/`;

    const history = initBrowserHistory();

    const store = initStore(history, rootSagas, initialState);

    initJam(store.dispatch);

    const render = (Component) =>
        ReactDOM.hydrate(
            <Provider store={store}>
                {/* provider needs the unbound version of i18next (see exports in i18next/ws/index.js) */}
                <ConnectedRouter history={history}>
                    <ErrorBoundary>
                        <Component />
                    </ErrorBoundary>
                </ConnectedRouter>
            </Provider>,
            document.getElementById('app'),
    );

    // Wait for current route to load
    // 1. Find current page
    let current: Route;
    for (const route of routes) {
        const { path, exact, strict } = route;
        const match = matchPath(history.location.pathname, { path, strict, exact });
        if (match) {
            current = route;
            break;
        }
    }
    // 2. Load reducers and sagas to store
    if (current) {
        if (!current.page && 'modules' in current) {
            const { default: Page } = await current.getPage();
            current.page = new Page();
        }
        await current.page.registerInStore(store);
    }

    // Wait for components load
    await Loadable.preloadReady();

    render(AppComponent);

    return render;
}
